import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, RotateCcw, Loader2, RefreshCw, Link2 } from "lucide-react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmtMoney = (n, c = "EUR") =>
  new Intl.NumberFormat("pt-PT", { style: "currency", currency: c }).format(Number(n || 0));
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

/** Inline modal: type a Moloni document number, server looks it up and
 *  attaches it to the session_id without re-issuing. Fixes the Sara Bessa
 *  case — WEB2/27444 already exists in Moloni; the reissue flow would
 *  emit a fresh number and double-bill the customer's account. */
const AssociateExistingModal = ({ tx, onClose, onDone }) => {
  const [number, setNumber] = useState("");
  const [kind, setKind] = useState("auto");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const n = parseInt(number.trim(), 10);
    if (!n || n < 1) {
      toast.error("Indica o número do documento (ex.: 27444 para WEB2/27444).");
      return;
    }
    setBusy(true);
    try {
      const r = await client.post(
        `/admin/invoices/${tx.session_id}/associate-existing`,
        { moloni_number: n, kind },
      );
      toast.success(`Documento Moloni ${r.data?.number || n} associado.`);
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível associar.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
         data-testid="associate-existing-modal" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#161920] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display text-xl text-[#F5F2EE] mb-1">Associar fatura existente</h3>
        <p className="text-sm text-[#A29F9B] mb-4">
          Liga a transação <code className="text-[#C5A880]">{tx.session_id.slice(0, 20)}…</code>
          {" "}({tx.user?.email || "sem cliente"}) a um documento que <strong>já existe</strong> no Moloni.
          Nada será re-emitido.
        </p>
        <label className="block text-xs uppercase tracking-wider text-[#A29F9B] mb-1">
          Número Moloni (ex.: 27444 para WEB2/27444)
        </label>
        <input type="number" min={1} value={number}
               onChange={(e) => setNumber(e.target.value)}
               className="w-full px-3 py-2 rounded-lg bg-[#0C0E12] border border-[#A29F9B]/40 focus:border-[#C5A880] outline-none text-[#F5F2EE] mb-3"
               placeholder="27444"
               autoFocus
               data-testid="associate-existing-number" />
        <label className="block text-xs uppercase tracking-wider text-[#A29F9B] mb-1">Tipo</label>
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-[#0C0E12] border border-[#A29F9B]/40 focus:border-[#C5A880] outline-none text-[#F5F2EE] mb-4"
                data-testid="associate-existing-kind">
          <option value="auto">Auto (tenta Fatura-Recibo → Recibo → Fatura)</option>
          <option value="invoiceReceipts">Fatura-Recibo (FR)</option>
          <option value="receipts">Recibo (RC)</option>
          <option value="invoices">Fatura (FT)</option>
        </select>
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="text-sm text-[#A29F9B] hover:text-[#F5F2EE] px-3 py-2"
                  data-testid="associate-existing-cancel">Cancelar</button>
          <button type="submit" disabled={busy}
                  className="text-sm inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#C5A880] text-[#0E1014] hover:bg-[#D4B690] disabled:opacity-50"
                  data-testid="associate-existing-submit">
            {busy && <Loader2 className="w-3 h-3 animate-spin" />}
            Associar
          </button>
        </div>
      </form>
    </div>
  );
};

/** Orphan payments panel — paid transactions WITHOUT a matching row in the
 *  invoices ledger. Repairs via the existing /admin/invoices/{session_id}/reissue
 *  endpoint (which already handles missing rows). Renders nothing when there
 *  are zero orphans — keeps the admin quiet on the happy path. */
const OrphanPaymentsSection = ({ onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState({});
  const [associatingTx, setAssociatingTx] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/invoices/orphan-payments");
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar órfãs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const repair = async (sessionId) => {
    if (!window.confirm(
      "Re-emitir Fatura+Recibo Moloni e gravar na BD? Se já existir documento " +
      "na Moloni com este session_id, o ?force pode emitir um novo (com novo " +
      "número fiscal). Confirma só se sabes o que estás a fazer."
    )) return;
    setRepairing((p) => ({ ...p, [sessionId]: true }));
    try {
      const r = await client.post(`/admin/invoices/${sessionId}/reissue`);
      if (r.data?.status === "already_issued") {
        toast.info(`Já existe documento Moloni (${r.data.invoice_number}). Refresca a tabela de faturas.`);
        await load();
        onChanged?.();
      } else {
        // The endpoint just schedules the Moloni call in the background — we
        // don't know yet if it will succeed. Poll the tx state briefly so we
        // can show an honest outcome (success / error / still pending) to
        // the admin, instead of the previous unconditional green toast.
        toast("Emissão agendada. A confirmar…", { icon: "⏳" });
        let final = null;
        for (let i = 0; i < 5; i++) {
          await new Promise((res) => setTimeout(res, 1200));
          try {
            const check = await client.get("/admin/invoices/orphan-payments");
            const row = (check.data?.rows || []).find((t) => t.session_id === sessionId);
            if (!row) { final = "issued"; break; }   // fell out of orphan list = success
            if (row.invoice_error) { final = "error"; break; }
            if (row.invoice_status === "issued") { final = "issued"; break; }
          } catch { /* ignore, will retry */ }
        }
        if (final === "issued") {
          toast.success("Fatura reconciliada com sucesso.");
        } else if (final === "error") {
          toast.error("Emissão falhou — lê a mensagem de erro na linha para saber porquê.");
        } else {
          toast.info("Ainda a processar. Refresca daqui a uns segundos para ver o resultado.");
        }
        await load();
        onChanged?.();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao reconciliar.");
    } finally {
      setRepairing((p) => ({ ...p, [sessionId]: false }));
    }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <section className="bg-[#161920] border border-amber-500/40 rounded-2xl p-6 md:p-8"
             data-testid="orphan-payments">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <span className="label-uppercase inline-flex items-center gap-2 text-amber-300">
            <AlertTriangle className="w-3.5 h-3.5" /> Reconciliação
          </span>
          <h2 className="font-display text-2xl md:text-3xl mt-2 text-[#F5F2EE]">
            Pagamentos sem fatura local
          </h2>
          <p className="text-sm text-[#A29F9B] mt-1 max-w-2xl">
            Transações pagas (Stripe ou EuPago) que <strong>não têm</strong> entrada
            correspondente em <code>db.invoices</code>. Se aparecer &ldquo;Erro Moloni&rdquo;,
            lê a mensagem — pode ser dados de faturação em falta.
            Clica em &ldquo;Reconciliar&rdquo; para reemitir e gravar, ou &ldquo;Associar&rdquo; se a fatura já existir no Moloni.
          </p>
        </div>
        <button onClick={load}
                className="text-xs text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-1.5"
                data-testid="orphan-refresh">
          <RefreshCw className="w-3 h-3" /> Refrescar
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-[#A29F9B]">A procurar...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#A29F9B] border-b border-white/10">
                <th className="py-2 pr-4">Pagamento</th>
                <th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Plano</th>
                <th className="py-2 pr-4 text-right">Montante</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.session_id}
                    className="border-b border-white/5"
                    data-testid={`orphan-row-${t.session_id}`}>
                  <td className="py-3 pr-4 text-[#DCD7D2] whitespace-nowrap">
                    {fmtDate(t.created_at)}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="text-[#F5F2EE]">{t.user?.name || (t.user_deleted ? <span className="italic text-rose-300">utilizador removido</span> : "—")}</div>
                    <div className="text-xs text-[#A29F9B] truncate max-w-[180px]">{t.user?.email || (t.user_deleted ? `id=${(t.user_id || "").slice(0,8)}` : "—")}</div>
                  </td>
                  <td className="py-3 pr-4 text-[#A29F9B]">{t.package_id || "—"}</td>
                  <td className="py-3 pr-4 text-right font-display">{fmtMoney(t.amount, t.currency?.toUpperCase())}</td>
                  <td className="py-3 pr-4">
                    {t.invoice_error ? (
                      <div className="max-w-[280px]">
                        <span className="inline-flex items-center gap-1 text-xs text-rose-300 font-semibold">
                          <AlertTriangle className="w-3 h-3" /> Erro Moloni
                        </span>
                        <div className="text-[11px] text-rose-200/80 mt-0.5 leading-tight break-words"
                             data-testid={`orphan-error-msg-${t.session_id}`}>
                          {t.invoice_error}
                        </div>
                      </div>
                    ) : t.invoice_status ? (
                      <span className="text-xs text-amber-300">{t.invoice_status}</span>
                    ) : (
                      <span className="text-xs text-[#A29F9B]">Sem registo</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setAssociatingTx(t)}
                              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20"
                              title="Associar um documento Moloni que já existe (não re-emite)"
                              data-testid={`orphan-associate-${t.session_id}`}>
                        <Link2 className="w-3 h-3" />
                        Associar
                      </button>
                      <button onClick={() => repair(t.session_id)}
                              disabled={repairing[t.session_id]}
                              className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#C5A880]/40 bg-[#C5A880]/10 text-[#C5A880] hover:bg-[#C5A880]/20 disabled:opacity-50"
                              title="Re-emitir uma nova Fatura-Recibo (usa novo número fiscal)"
                              data-testid={`orphan-repair-${t.session_id}`}>
                        {repairing[t.session_id] ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3 h-3" />
                        )}
                        Reconciliar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {associatingTx && (
        <AssociateExistingModal
          tx={associatingTx}
          onClose={() => setAssociatingTx(null)}
          onDone={async () => { await load(); onChanged?.(); }}
        />
      )}
    </section>
  );
};

export default OrphanPaymentsSection;
